'use client';

import * as React from 'react';
import { authClient } from '@/lib/auth/custom/client';
import { logger } from '@/lib/default-logger';
import { useNavigate } from 'react-router-dom';

export const UserContext = React.createContext(undefined);

export function UserProvider({ children }) {
  const navigate = useNavigate();
  const [state, setState] = React.useState({
    user: null,
    error: null,
    isLoading: true,
    loggedIn: false
  });

  const checkSession = React.useCallback(async () => {
    try {
      const response = await authClient.getUser();

      if (response.ok) {
        const { data } = await response.json();
        setLoggedIn(true);
        setUser(data.username);
      } else if (response.status === 401 || response.status === 403) {
        setLoggedIn(false);
        setUser(null);
        localStorage.clear();
      } else {
        setLoggedIn(false);
        setUser(null);
        localStorage.clear();
      }
    } catch (error) {
      console.error("Error checking session:", error);
      setLoggedIn(false);
      setUser(null);
      localStorage.clear();
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogout = React.useCallback(async () => {
    try {
      const response = await authClient.signOut();
      if (response.error) {
        logger.error(response.error);
        return;
      }

      setState({ user: null, error: null, isLoading: false, loggedIn: false });
      localStorage.clear();
    } catch (error) {
      logger.error('Error during logout:', error);
    }
  }, []);

  React.useEffect(() => {
    checkSession().catch((err) => {
      logger.error(err);
    });

    React.useEffect(() => {
      if (!state.loggedIn && !state.isLoading) {
        navigate(paths.auth.custom.signIn); 
      }
    }, [state.loggedIn, state.isLoading, navigate]);

  }, [checkSession]);

  const setUser = (user) => setState((prev) => ({ ...prev, user }));

  const setLoggedIn = (loggedIn) => setState((prev) => ({ ...prev, loggedIn }));

  return (
    <UserContext.Provider value={{ ...state, checkSession, handleLogout, setUser, setLoggedIn }}>
      {children}
    </UserContext.Provider>
  );
}

export const UserConsumer = UserContext.Consumer;